import type { Supplier } from "@/apis/gql/types";
import {
  TableActionBarButton,
  TableActionBarRoot,
  TableActionBarRowsSelected,
} from "@/components/shared/data-table/action-bar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetAllDatevTokens } from "@/queries/datev-tokens/get-all";
import type { Table } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const TableActionBar = ({
  type,
  table,
  onDelete,
  onExportCsv,
  onExportDatev,
}: {
  type: "creditor" | "debtor";
  table: Table<Supplier>;
  onDelete: (ids: string[]) => void;
  onExportCsv: (ids: string[]) => void;
  onExportDatev: (ids: string[]) => void;
}) => {
  const { t } = useTranslation();

  const rows = table.getSelectedRowModel().rows;

  const rowIds = useMemo(() => {
    return rows.map((row) => row.id);
  }, [rows]);

  const handleDelete = () => {
    onDelete(rowIds);
  };

  const handleExportCsv = () => {
    onExportCsv(rowIds);
  };

  const handleExportDatev = () => {
    onExportDatev(rowIds);
  };

  if (rows.length === 0) return null;

  return (
    <TableActionBarRoot className="bottom-20">
      <TableActionBarRowsSelected table={table} />
      <div className="flex gap-2">
        <TableActionBarButton variant="outline" onClick={handleDelete}>
          {t("delete")}
        </TableActionBarButton>
        {type === "creditor" && (
          <CounterpartiesExportButton
            onExportCsv={handleExportCsv}
            onExportDatev={handleExportDatev}
          />
        )}
      </div>
    </TableActionBarRoot>
  );
};

const CounterpartiesExportButton = ({
  onExportCsv,
  onExportDatev,
}: {
  onExportCsv: () => void;
  onExportDatev: () => void;
}) => {
  const { t } = useTranslation();

  const { tokens } = useGetAllDatevTokens();
  const token = tokens?.[0] ?? null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">{t("export")}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72" side="top" sideOffset={20}>
        {token && (
          <DropdownMenuItem onSelect={() => onExportDatev()}>
            {t("suppliers.exports.buchungsdatenservice")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => onExportCsv()}>
          {t("suppliers.exports.csv")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
